import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { AgentCompatibilityRadarScores } from "@/api/generated";
import { CompatibilityScoreDisplay } from "@/components/agent/CompatibilityScoreDisplay";
import { CustomButton } from "@/components/Button";
import { CompatibilityRadarChart } from "@/components/compatibility_radar_chart/CompatibilityRadarChart";
import { PLANNER_EASING, PLANNER_MOTION } from "@/components/planner_onboarding/plannerMotion";
import {
  PlannerReveal,
  usePlannerActionReady,
  usePlannerReducedMotion,
  usePlannerSampleScore,
} from "@/components/planner_onboarding/PlannerReveal";
import { plannerIntroductionActionStyles } from "@/layouts/PlannerIntroductionLayout";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  "@keyframes radarExpand": {
    from: { transform: "scale(0)", opacity: 0 },
    to: { transform: "scale(1)", opacity: 1 },
  },
  radarExpand: {
    "& .planner-compatibility-series .recharts-polygon": {
      // 非対称な図形の外接矩形ではなく、グラフの中心を拡大の起点にする。
      transformBox: "view-box",
      transformOrigin: "50% 50%",
      vectorEffect: "non-scaling-stroke",
      animation: `$radarExpand ${PLANNER_MOTION.graphDuration}ms ${PLANNER_EASING} ${PLANNER_MOTION.graphDraw}ms both`,
    },
    "@media (prefers-reduced-motion: reduce), print": {
      "&& .planner-compatibility-series .recharts-polygon": { animation: "none", transform: "none", opacity: 1 },
    },
  },
  heading: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: 1.3,
    textAlign: "center",
    [theme.breakpoints.up("md")]: { fontSize: 24 },
  },
  headingAccent: { color: theme.palette.semantic["action/primary"] },
  description: {
    marginTop: 12,
    marginBottom: 24,
    fontSize: 14,
    lineHeight: 1.5,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  card: { borderRadius: 16, backgroundColor: theme.palette.semantic["surface/card"], padding: 20 },
  cardTitle: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.3,
    color: theme.palette.semantic["text/heading"],
    marginBottom: 16,
    textAlign: "center",
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  chartArea: { display: "flex", justifyContent: "center" },
  chartCaption: { fontSize: 14, lineHeight: 1.5, marginTop: 16, textAlign: "center", [theme.breakpoints.up("md")]: { fontSize: 16 } },
  example: { fontSize: 12, color: theme.palette.semantic["text/secondary"], textAlign: "center", marginTop: 12 },
  score: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 8 },
  scoreLabel: { fontSize: 14, fontWeight: 700 },
  almostDone: { fontSize: 14, fontWeight: 700, textAlign: "center", marginBottom: 12 },
  chartSampleNote: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.semantic["text/secondary"],
    marginTop: 8,
    textAlign: "center",
  },
  divider: { borderTop: `1px solid ${theme.palette.semantic["border/default"]}`, margin: "16px 0" },
  conditionList: { display: "flex", flexWrap: "wrap", gap: 8 },
  conditionChip: {
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    maxWidth: "100%",
    borderRadius: 4,
    backgroundColor: theme.palette.semantic["state/selected-bg"],
    padding: "4px 8px",
  },
  conditionLabel: { fontSize: 12, lineHeight: 1.5, color: theme.palette.semantic["text/secondary"] },
  conditionValue: { fontSize: 14, lineHeight: 1.3, fontWeight: 700, overflowWrap: "anywhere" },
  action: { ...plannerIntroductionActionStyles },
}));

export interface ConditionItem {
  label: string;
  value: string;
}

interface DiagnosisCompatibilityAgentsIntroProps {
  animate?: boolean;
  radarScores: AgentCompatibilityRadarScores;
  conditionItems: ConditionItem[];
  onClickViewCompatibleAgents: () => void;
}

export const DiagnosisCompatibilityAgentsIntro: React.FC<DiagnosisCompatibilityAgentsIntroProps> = ({
  animate = true,
  radarScores,
  conditionItems,
  onClickViewCompatibleAgents,
}) => {
  const classes = useStyles();
  const systemReduced = usePlannerReducedMotion();
  const reduced = systemReduced || !animate;
  const ready = usePlannerActionReady(PLANNER_MOTION.next, reduced);
  const score = usePlannerSampleScore(reduced);

  return (
    <>
      <PlannerReveal animate={!reduced}>
        <Typography component="h1" className={classes.heading}>
          最後は、<span className={classes.headingAccent}>気になる認定プランナー</span>と
          <br />マッチしてみましょう！
        </Typography>
        <Typography className={classes.description}>
          診断結果と各プランナーの得意な領域を照らし合わせ、相性が高い順に紹介します。
        </Typography>
      </PlannerReveal>

      <PlannerReveal delay={PLANNER_MOTION.card} animate={!reduced} className={classes.card}>
        <Typography className={classes.cardTitle}>マッチングに使用する条件</Typography>
        <PlannerReveal delay={PLANNER_MOTION.graph} animate={!reduced}>
          <Box className={`${classes.chartArea} ${!reduced ? classes.radarExpand : ""}`}>
            <CompatibilityRadarChart scores={radarScores} size="compact" variant="introduction" />
          </Box>
          <Typography className={classes.chartSampleNote}>※図はイメージです</Typography>
        </PlannerReveal>
        <PlannerReveal delay={PLANNER_MOTION.score} animate={!reduced}>
          <Typography className={classes.chartCaption}>
            項目ごとの一致度を平均したものが、<br /><b>相性スコア</b>です。
          </Typography>
          <Typography className={classes.example}>例：</Typography>
          <Box className={classes.score}>
            <Typography className={classes.scoreLabel}>あなたとの相性スコア</Typography>
            <CompatibilityScoreDisplay score={score} rank={1} size="L" crownSize={24} />
          </Box>
        </PlannerReveal>

        <Box className={classes.divider} />
        <PlannerReveal delay={PLANNER_MOTION.tags - 100} animate={!reduced}>
          <Typography className={classes.cardTitle}>あなたの回答</Typography>
        </PlannerReveal>

        <Box className={classes.conditionList}>
          {conditionItems.map((item, index) => (
            <PlannerReveal
              key={item.label}
              delay={PLANNER_MOTION.tags + index * PLANNER_MOTION.tagStagger}
              animate={!reduced}
              className={classes.conditionChip}
            >
              <Typography component="span" className={classes.conditionLabel}>
                {item.label}
              </Typography>
              <Typography component="span" className={classes.conditionValue}>
                {item.value}
              </Typography>
            </PlannerReveal>
          ))}
        </Box>
      </PlannerReveal>

      <PlannerReveal delay={PLANNER_MOTION.next} animate={!reduced} className={classes.action}>
        <Typography className={classes.almostDone}>あとちょっとです！</Typography>
        <CustomButton tone="primary" buttonSize="L" fullWidth disabled={!ready} onClick={onClickViewCompatibleAgents}>
          次へ
        </CustomButton>
      </PlannerReveal>
    </>
  );
};
